import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import apiClient from '../config/axios';
import config from '../config/config';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getLocalDateString } from '../utils/dateUtils';
import * as XLSX from 'xlsx';
import ActionMenu from '../components/ActionMenu';
import Pagination from '../components/Pagination';
import TransactionLoader from '../components/TransactionLoader';
import './Report.css';

const PurchaseReport = () => {
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [pagination, setPagination] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showBillDetailsModal, setShowBillDetailsModal] = useState(false);
  const [billDetails, setBillDetails] = useState(null);
  const [loadingBillDetails, setLoadingBillDetails] = useState(false);
  const [partyNameFilter, setPartyNameFilter] = useState('');

  const abortControllerRef = useRef(null);

  // UI-only filter by party name (no API call)
  const partyFilterTrimmed = (partyNameFilter || '').trim().toLowerCase();
  const filteredTransactions = partyFilterTrimmed
    ? transactions.filter(t => (t.party_name || '').toLowerCase().includes(partyFilterTrimmed))
    : transactions;
  const filteredSummary = summary && filteredTransactions.length !== transactions.length
    ? (() => {
        let totalPurchase = 0, totalPaid = 0, totalBalance = 0;
        filteredTransactions.forEach(t => {
          totalPurchase += parseFloat(t.total_amount) || 0;
          totalPaid += parseFloat(t.paid_amount) || 0;
          totalBalance += parseFloat(t.balance_amount != null ? t.balance_amount : (t.total_amount || 0) - (t.paid_amount || 0)) || 0;
        });
        return {
          totalPurchase,
          totalPaid,
          totalBalance,
          totalTransactions: filteredTransactions.length
        };
      })()
    : summary;

  const validateDates = () => {
    if (fromDate && toDate && fromDate > toDate) {
      alert('From date cannot be after To date. Please select valid dates.');
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (validateDates()) {
      fetchReport();
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, page, limit]);

  const fetchReport = async () => {
    if (!validateDates()) return;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    try {
      setLoading(true);
      const from = getLocalDateString(fromDate);
      const to = getLocalDateString(toDate);
      const response = await apiClient.get(config.api.purchasesReport, {
        params: { from_date: from, to_date: to, page, limit },
        signal: abortController.signal
      });
      if (!abortController.signal.aborted) {
        setTransactions(response.data.transactions);
        setSummary(response.data.summary);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') return;
      console.error('Error fetching purchase report:', error);
    } finally {
      if (!abortController.signal.aborted) setLoading(false);
    }
  };

  const fetchBillDetails = async (billNumber) => {
    try {
      setLoadingBillDetails(true);
      const response = await apiClient.get(config.api.purchasesBillDetails(billNumber));
      setBillDetails(response.data);
      setShowBillDetailsModal(true);
    } catch (error) {
      console.error('Error fetching purchase bill details:', error);
      alert('Error fetching bill details. Please try again.');
    } finally {
      setLoadingBillDetails(false);
    }
  };

  const exportToExcel = () => {
    if (exporting || filteredTransactions.length === 0) return;
    setExporting(true);
    try {
      const data = filteredTransactions.map(txn => ({
        'Date': new Date(txn.created_at).toLocaleString(),
        'Bill Number': txn.bill_number,
        'Party Name': txn.party_name,
        'Total Amount': Math.round(parseFloat(txn.total_amount || 0) * 100) / 100,
        'Paid Amount': Math.round(parseFloat(txn.paid_amount || 0) * 100) / 100,
        'Balance Amount': Math.round(parseFloat(txn.balance_amount != null ? txn.balance_amount : (parseFloat(txn.total_amount || 0) - parseFloat(txn.paid_amount || 0))) * 100) / 100,
        'Payment Status': (txn.payment_status || '').replace('_', ' ').toUpperCase()
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const colWidths = [];
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        let maxWidth = 10;
        for (let R = range.s.r; R <= range.e.r; ++R) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = ws[cellAddress];
          if (cell && cell.v) {
            const cellLength = String(cell.v).length;
            if (cellLength > maxWidth) maxWidth = cellLength;
          }
        }
        colWidths.push({ wch: Math.min(maxWidth + 2, 50) });
      }
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Purchase Report');
      const from = getLocalDateString(fromDate);
      const to = getLocalDateString(toDate);
      XLSX.writeFile(wb, `purchase_report_${from}_${to}.xlsx`);
      alert('Excel file exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <TransactionLoader isLoading={loading || loadingBillDetails} type="transaction" message={loading ? 'Loading purchase report...' : loadingBillDetails ? 'Loading bill details...' : ''} />
      <div className="report">
        <div className="report-header">
          <h2>Purchase Report</h2>
          <button
            onClick={exportToExcel}
            className="btn btn-success"
            disabled={exporting || filteredTransactions.length === 0}
            style={{
              opacity: (exporting || filteredTransactions.length === 0) ? 0.6 : 1,
              cursor: (exporting || filteredTransactions.length === 0) ? 'not-allowed' : 'pointer'
            }}
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        </div>

        <div className="card">
          <div className="date-filters">
            <div className="form-group">
              <label>From Date</label>
              <DatePicker
                selected={fromDate}
                onChange={(date) => {
                  setFromDate(date);
                  if (date && toDate && date > toDate) setToDate(date);
                }}
                dateFormat="dd-MM-yyyy"
                className="date-input"
                maxDate={toDate}
              />
            </div>
            <div className="form-group">
              <label>To Date</label>
              <DatePicker
                selected={toDate}
                onChange={(date) => {
                  setToDate(date);
                  if (date && fromDate && date < fromDate) setFromDate(date);
                }}
                dateFormat="dd-MM-yyyy"
                className="date-input"
                minDate={fromDate}
              />
            </div>
            <div className="form-group">
              <label>Party Name (filter)</label>
              <input
                type="text"
                placeholder="Filter by party name..."
                value={partyNameFilter}
                onChange={(e) => setPartyNameFilter(e.target.value)}
                className="date-input"
                style={{ minWidth: '180px' }}
              />
            </div>
            <button
              onClick={() => {
                setFromDate(new Date());
                setToDate(new Date());
                setPartyNameFilter('');
                setPage(1);
                setLimit(50);
              }}
              className="btn btn-primary"
              disabled={loading}
            >
              Refresh
            </button>
            <div className="form-group">
              <label>Records per page</label>
              <select
                value={limit >= (pagination?.totalRecords || 0) ? 'all' : limit}
                onChange={(e) => {
                  const newLimit = e.target.value === 'all' ? (pagination?.totalRecords || 10000) : Number(e.target.value);
                  setLimit(newLimit);
                  setPage(1);
                }}
                className="date-input"
                disabled={loading}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value="all">All ({pagination?.totalRecords || 0} records)</option>
              </select>
            </div>
          </div>
        </div>

        {filteredSummary && (
          <div className="card summary-card">
            <h3>Summary{partyFilterTrimmed ? ' (filtered by party)' : ''}</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <label>Total Purchase:</label>
                <span>₹{filteredSummary.totalPurchase.toFixed(2)}</span>
              </div>
              <div className="summary-item">
                <label>Total Paid:</label>
                <span>₹{filteredSummary.totalPaid.toFixed(2)}</span>
              </div>
              <div className="summary-item">
                <label>Total Balance:</label>
                <span>₹{filteredSummary.totalBalance.toFixed(2)}</span>
              </div>
              <div className="summary-item">
                <label>Total Transactions:</label>
                <span>{filteredSummary.totalTransactions}</span>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="table-container">
            <table className="table sell-report-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'center', padding: '12px 8px' }}>Date</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px' }}>Bill Number</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px' }}>Party Name</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px' }}>Total Amount</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px' }}>Paid Amount</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px' }}>Balance Amount</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px' }}>Payment Status</th>
                  <th style={{ textAlign: 'center', padding: '12px 8px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center' }}>{transactions.length === 0 ? 'No purchase transactions found' : 'No party matches the filter'}</td>
                  </tr>
                ) : (
                  filteredTransactions.map((txn) => (
                    <tr key={txn.id}>
                      <td style={{ textAlign: 'center' }}>{new Date(txn.created_at).toLocaleString()}</td>
                      <td style={{ textAlign: 'center' }}>{txn.bill_number}</td>
                      <td style={{ textAlign: 'center' }}>{txn.party_name}</td>
                      <td style={{ textAlign: 'center' }}>₹{parseFloat(txn.total_amount || 0).toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>₹{parseFloat(txn.paid_amount || 0).toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>
                        ₹{(txn.balance_amount != null ? parseFloat(txn.balance_amount) : parseFloat(txn.total_amount || 0) - parseFloat(txn.paid_amount || 0)).toFixed(2)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`status-badge ${txn.payment_status || ''}`}>
                          {(txn.payment_status || '').replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <ActionMenu
                            actions={[
                              {
                                label: 'View',
                                icon: '👁️',
                                onClick: () => fetchBillDetails(txn.bill_number)
                              }
                            ]}
                            itemId={txn.id}
                            itemName={txn.bill_number}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {pagination && (
              <Pagination
                currentPage={page}
                totalPages={pagination.totalPages}
                onPageChange={(newPage) => {
                  if (!loading) {
                    setPage(newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                totalRecords={pagination.totalRecords}
                showTotalRecords={true}
              />
            )}
          </div>
        )}

        {/* Bill Details Modal */}
        {showBillDetailsModal && billDetails && (
          <div
            className="modal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) e.stopPropagation();
            }}
          >
            <div className="modal-content" style={{ maxWidth: '1000px', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Purchase Bill Details - {billDetails.bill_number}</h3>
                <button
                  className="btn-close"
                  onClick={() => {
                    setShowBillDetailsModal(false);
                    setBillDetails(null);
                  }}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                {loadingBillDetails ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
                ) : (
                  <>
                    <div style={{ marginBottom: '30px' }}>
                      <h4 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>Bill Information</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                        <div><strong>Bill Number:</strong> {billDetails.bill_number}</div>
                        <div><strong>Transaction ID:</strong> {billDetails.transaction_id}</div>
                        <div><strong>Transaction Date:</strong> {new Date(billDetails.transaction_date).toLocaleDateString()}</div>
                        <div><strong>Created At:</strong> {new Date(billDetails.created_at).toLocaleString()}</div>
                      </div>
                    </div>

                    {billDetails.party && (
                      <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>Party (Buyer)</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                          <div><strong>Party Name:</strong> {billDetails.party.party_name}</div>
                          <div><strong>Mobile:</strong> {billDetails.party.mobile_number || '-'}</div>
                          <div><strong>Email:</strong> {billDetails.party.email || '-'}</div>
                          <div><strong>Address:</strong> {billDetails.party.address || '-'}</div>
                          <div><strong>GST Number:</strong> {billDetails.party.gst_number || '-'}</div>
                        </div>
                      </div>
                    )}

                    {billDetails.items && billDetails.items.length > 0 && (
                      <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>Items ({billDetails.items.length})</h4>
                        <div style={{ overflowX: 'auto' }}>
                          <table className="table" style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: '#f8f9fa' }}>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>S.No</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Product Name</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Product Code</th>
                                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Brand</th>
                                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Qty</th>
                                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Purchase Rate</th>
                                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {billDetails.items.map((item, index) => (
                                <tr key={item.item_id} style={{ borderBottom: '1px solid #e9ecef' }}>
                                  <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle' }}>{index + 1}</td>
                                  <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle' }}>{item.product_name}</td>
                                  <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle' }}>{item.product_code || '-'}</td>
                                  <td style={{ padding: '12px', textAlign: 'left', verticalAlign: 'middle' }}>{item.brand || '-'}</td>
                                  <td style={{ padding: '12px', textAlign: 'right', verticalAlign: 'middle' }}>{item.quantity ?? 0}</td>
                                  <td style={{ padding: '12px', textAlign: 'right', verticalAlign: 'middle', fontVariantNumeric: 'tabular-nums' }}>
                                    ₹{item.purchase_rate != null ? Math.round(parseFloat(item.purchase_rate) * 100) / 100 : '0.00'}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', verticalAlign: 'middle', fontVariantNumeric: 'tabular-nums' }}>
                                    ₹{item.total_amount != null ? Math.round(parseFloat(item.total_amount) * 100) / 100 : '0.00'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {billDetails.summary && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>Summary</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', background: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                          <div><strong>Total Amount:</strong> ₹{billDetails.summary.total_amount != null ? Math.round(parseFloat(billDetails.summary.total_amount) * 100) / 100 : '0.00'}</div>
                          <div><strong>Paid Amount:</strong> ₹{billDetails.summary.paid_amount != null ? Math.round(parseFloat(billDetails.summary.paid_amount) * 100) / 100 : '0.00'}</div>
                          <div><strong>Balance Amount:</strong> ₹{billDetails.summary.balance_amount != null ? Math.round(parseFloat(billDetails.summary.balance_amount) * 100) / 100 : '0.00'}</div>
                          <div>
                            <strong>Payment Status:</strong>
                            <span className={`status-badge ${billDetails.summary.payment_status || ''}`} style={{ marginLeft: '10px' }}>
                              {(billDetails.summary.payment_status || '').replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          <div><strong>Total Items:</strong> {billDetails.summary.total_items ?? 0}</div>
                          <div><strong>Total Quantity:</strong> {billDetails.summary.total_quantity ?? 0}</div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            fontSize: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
            zIndex: 1000,
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#2980b9';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#3498db';
            e.target.style.transform = 'scale(1)';
          }}
          title="Scroll to top"
        >
          ↑
        </button>
      </div>
    </Layout>
  );
};

export default PurchaseReport;
